import { Flex } from 'antd';
import DateRangeFilter from '@/components/cards/FilterCard/dateRangeFilter/DateRangeFilter';
import FiltersMultiSelectEcho from '@/components/cards/FilterCard/FiltersMultiSelectEcho';
import type { DateRange } from '@/types';
import type { BaseShow } from '@/types/api/network';

type ShowsAndDateSettings = {
  shows: string[];
  dateRange: DateRange;
};

interface DownloadShowDateFiltersProps<TSettings extends ShowsAndDateSettings> {
  shows: BaseShow[];
  downloadSettings: TSettings;
  setDownloadSettings: (settings: TSettings | ((previous: TSettings) => TSettings)) => void;
  onDateOpen?: () => void;
  onDatePresetSelect?: (preset: string) => void;
  onDateApply?: (from: string, to: string) => void;
}

/**
 * Show multi-select and date range shared by the content performance and audience download drawers.
 * Reuses {@link FiltersMultiSelectEcho} so empty selection means all shows (same as the main
 * page filter bar).
 */
const DownloadShowDateFilters = <TSettings extends ShowsAndDateSettings>({
  shows,
  downloadSettings,
  setDownloadSettings,
  onDateOpen,
  onDatePresetSelect,
  onDateApply,
}: DownloadShowDateFiltersProps<TSettings>) => {
  return (
    <Flex vertical gap={24} style={{ width: '100%' }}>
      <FiltersMultiSelectEcho
        labelSingular="show"
        items={shows}
        selectedIds={downloadSettings.shows}
        allowClear
        onChange={(selectedShowIds) => {
          setDownloadSettings((previous) => ({
            ...previous,
            shows: selectedShowIds,
          }));
        }}
      />
      <DateRangeFilter
        dateRange={{
          from: downloadSettings.dateRange.from,
          to: downloadSettings.dateRange.to,
        }}
        onOpen={onDateOpen}
        onPresetSelect={onDatePresetSelect}
        onApply={(from, to) => {
          setDownloadSettings((previous) => ({
            ...previous,
            dateRange: { from, to },
          }));
          onDateApply?.(from, to);
        }}
      />
    </Flex>
  );
};

export default DownloadShowDateFilters;
